import { useMemo, useState } from 'react'
import { Button, Chip, Table, ToggleButton, ToggleButtonGroup, Tooltip } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { FilterSelect } from '@/components/ui/filter-select'
import { DownloadIcon } from '@/components/ui/icons'
import { useTenant } from '@/features/business'
import { booksToday, excelMomentLines, excelReportFileName, previewExcel } from '@/features/reports'
import { PAGE_LIMIT_MAX, translated } from '@/lib/api/common'

import {
  useInventoryLocations,
  useReasonCodes,
  useStockItems,
  useStockItemsById,
  useStockMovements,
  useStockOnHand,
  useUnitCatalog,
} from '../hooks'
import type { MovementFilters, UnitCatalog } from '../hooks'
import type {
  InventoryLocation,
  MovementSourceType,
  MovementType,
  ReasonCode,
  StockItem,
  StockLevel,
  StockMovement,
  UnitOfMeasure,
} from '../types'
import {
  dateTime,
  isCounted,
  itemTypeLabels,
  locationLabel,
  movementTypeLabels,
  quantity,
  stockStatus,
  stockStatusLabels,
  stockValue,
} from './inventory-format'
import type { StockStatus } from './inventory-format'

const PAGE_SIZE = 10

/** Where the lists' item names come from: one page of items, as large as the API allows. */
const ITEM_NAMES_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

type OverviewView = 'stock' | 'movements'

const TOGGLE_CLASS =
  'data-[selected]:bg-background data-[selected]:text-foreground px-4 text-xs data-[selected]:shadow-sm'

interface StockOverviewPanelProps {
  /** The location picked in the bar, or `null` for every location. */
  locationId: string | null
}

/**
 * Where stock stands, and how it got there.
 *
 * Two lists over the same location filter: what is on hand — each item at each
 * location, with what it is worth — and the ledger of every movement in and out,
 * newest first. The ledger is the record; the levels are what it adds up to.
 */
export function StockOverviewPanel({ locationId }: StockOverviewPanelProps) {
  const [view, setView] = useState<OverviewView>('stock')
  /** Kept here rather than in the ledger, so it survives a look at the levels. */
  const [type, setType] = useState<MovementType | null>(null)

  const { locations } = useInventoryLocations()
  const location = locations.find((candidate) => candidate.id === locationId) ?? null

  return (
    <>
      <ModuleIntro
        description={
          location
            ? `What is on hand at ${locationLabel(location)}, and every movement in and out of it, newest first.`
            : 'What is on hand at each of your locations, and every movement in and out, newest first.'
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <ToggleButtonGroup
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([view])}
          onSelectionChange={(keys) => {
            const [first] = keys as Set<string>
            if (first) setView(first as OverviewView)
          }}
          aria-label="What to list"
        >
          <ToggleButton id="stock" size="sm" className={TOGGLE_CLASS}>
            On hand
          </ToggleButton>
          <ToggleButton id="movements" size="sm" className={TOGGLE_CLASS}>
            Movement ledger
          </ToggleButton>
        </ToggleButtonGroup>

        {/* True of the service, not just of this screen: a transfer leaves its
            source when shipped and reaches its destination only when received. */}
        {view === 'stock' && (
          <p className="text-muted ms-auto text-xs">
            On hand is derived from the ledger — never edited here. In-transit stock is excluded
            until receipt.
          </p>
        )}
      </div>

      {view === 'stock' ? (
        <StockOnHand locationId={locationId} locations={locations} />
      ) : (
        <Movements filters={{ locationId, type }} locations={locations} onTypeChange={setType} />
      )}
    </>
  )
}

/** The page belongs to what it was turned under; anything new starts on the first. */
function usePageFor(scope: string) {
  const [page, setPage] = useState(0)
  const [pageOf, setPageOf] = useState(scope)

  if (pageOf !== scope) {
    setPageOf(scope)
    setPage(0)
  }

  return [page, setPage] as const
}

/**
 * The items a page names, by id.
 *
 * The lists themselves come from `/stock` and `/movements`, which carry only an
 * item id. The names come from one page of `/items` — the same query the
 * dialogs' pickers make, so it is usually cached already — and an item past
 * that page is read by its id, after the page has answered and only then.
 */
function useItemsOf(rows: readonly { itemId: string }[]): ReadonlyMap<string, StockItem> {
  const { items: firstPage, isLoading } = useStockItems(ITEM_NAMES_QUERY)

  const known = useMemo(() => new Map(firstPage.map((item) => [item.id, item])), [firstPage])
  const missing = useMemo(
    () =>
      isLoading ? [] : [...new Set(rows.map((row) => row.itemId))].filter((id) => !known.has(id)),
    [rows, known, isLoading],
  )
  const rest = useStockItemsById(missing)

  return useMemo(() => new Map([...known, ...rest]), [known, rest])
}

/** The location's name, or a dash for one closed since — the list is active ones. */
function locationName(id: string, locations: readonly InventoryLocation[]): string {
  const location = locations.find((candidate) => candidate.id === id)
  return location ? locationLabel(location) : '—'
}

interface StockOnHandProps {
  locationId: string | null
  locations: readonly InventoryLocation[]
}

function StockOnHand({ locationId, locations }: StockOnHandProps) {
  const [page, setPage] = usePageFor(locationId ?? '')
  const { levels, total, isLoading, isRefreshing, error, retry } = useStockOnHand(
    locationId,
    page,
    PAGE_SIZE,
  )
  const items = useItemsOf(levels)

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState subject="your stock" error={error} onRetry={retry} />
  if (levels.length === 0) {
    return (
      <EmptyState
        title={locationId ? 'Nothing held here yet.' : 'No stock yet.'}
        description="An item shows here once stock has moved at a location — received, sold, counted or transferred."
      />
    )
  }

  return (
    <>
      <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
        <StockOnHandTable
          levels={levels}
          items={items}
          locations={locations}
          showLocation={!locationId}
        />
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
    </>
  )
}

/** `257.85` — measured stock to at least two decimals, so a column of weights lines up. */
const measuredFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

/**
 * `257.85 kg`, `409 each`. A counted unit has no fractions worth padding — four
 * hundred buns are `409`, not `409.00` — so only weights and volumes get two.
 */
function measured(value: number, unit: UnitOfMeasure, units: UnitCatalog): string {
  if (units.get(unit)?.family === 'count') return `${quantity(value)} ${unit}`

  const sign = value < 0 ? '−' : ''
  return `${sign}${measuredFormat.format(Math.abs(value))} ${unit}`
}

const stockStatusColors = {
  in: 'success',
  low: 'warning',
  recipe: 'accent',
  out: 'danger',
  untracked: 'default',
} as const satisfies Record<StockStatus, string>

/** The status, with a dot in its colour so it reads before the word does. */
function StatusChip({ status }: { status: StockStatus }) {
  return (
    <Chip size="sm" variant="soft" color={stockStatusColors[status]}>
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      {stockStatusLabels[status]}
    </Chip>
  )
}

/** A quiet dash: no figure for this cell, and no zero pretending to be one. */
function Dash() {
  return <span className="text-muted">—</span>
}

/** Figures in the monospace stack, right-aligned, so the decimals line up down the column. */
const FIGURE_CLASS = 'block text-right font-mono text-xs tabular-nums'

interface StockOnHandTableProps {
  levels: StockLevel[]
  items: ReadonlyMap<string, StockItem>
  locations: readonly InventoryLocation[]
  /** Every location at once lists an item once per location, so the rows need telling apart. */
  showLocation: boolean
}

function StockOnHandTable({ levels, items, locations, showLocation }: StockOnHandTableProps) {
  const { catalog: units } = useUnitCatalog()

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Stock on hand">
          <Table.Header>
            <Table.Column id="item" isRowHeader>
              Item
            </Table.Column>
            {showLocation && <Table.Column id="location">Location</Table.Column>}
            <Table.Column id="type">Type</Table.Column>
            <Table.Column id="category">Category</Table.Column>
            <Table.Column id="onHand" className="text-right">
              On hand
            </Table.Column>
            <Table.Column id="reserved" className="text-right">
              Reserved
            </Table.Column>
            <Table.Column id="available" className="text-right">
              Available
            </Table.Column>
            <Table.Column id="value" className="text-right">
              Value
            </Table.Column>
            <Table.Column id="status">Status</Table.Column>
          </Table.Header>

          <Table.Body>
            {levels.map((level) => {
              const item = items.get(level.itemId)
              const counted = item ? isCounted(item) : false

              return (
                <Table.Row
                  key={`${level.itemId}-${level.locationId}`}
                  id={`${level.itemId}-${level.locationId}`}
                >
                  <Table.Cell>
                    <span className="font-medium">{item?.name ?? '…'}</span>
                  </Table.Cell>

                  {showLocation && (
                    <Table.Cell>
                      <span className="text-muted">
                        {locationName(level.locationId, locations)}
                      </span>
                    </Table.Cell>
                  )}

                  <Table.Cell>
                    <span className="text-muted">
                      {item ? itemTypeLabels[item.stockItemType] : '…'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="text-muted">{item?.category?.name ?? '—'}</span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className={`${FIGURE_CLASS} ${level.onHand < 0 ? 'text-danger' : ''}`}>
                      {item ? measured(level.onHand, item.unitOfMeasure, units) : '…'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className={`${FIGURE_CLASS} text-muted`}>
                      {item ? measured(level.reserved, item.unitOfMeasure, units) : '…'}
                    </span>
                  </Table.Cell>

                  {/* On hand less reserved — what can still be sold or used. Below
                      zero when a count finds less than was already promised. */}
                  <Table.Cell>
                    <span className={`${FIGURE_CLASS} ${level.available < 0 ? 'text-danger' : ''}`}>
                      {item ? measured(level.available, item.unitOfMeasure, units) : '…'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className={FIGURE_CLASS}>{stockValue(level.stockValue)}</span>
                  </Table.Cell>

                  <Table.Cell>
                    {item ? (
                      <StatusChip
                        status={counted ? stockStatus(item, level.available, true) : 'untracked'}
                      />
                    ) : (
                      <Dash />
                    )}
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}

/**
 * `Sale deduction`, `Waste · Spoiled`, `Count variance`, `Transfer in`. A waste or
 * adjustment row says why, from its reason code; a sale's reversal what undid it.
 */
function movementLabel(movement: StockMovement, reasons: ReadonlyMap<string, ReasonCode>): string {
  const reason = movement.reasonCodeId ? reasons.get(movement.reasonCodeId)?.name : undefined

  if (movement.type === 'WASTE') {
    return reason ? `${movementTypeLabels.WASTE} · ${reason}` : movementTypeLabels.WASTE
  }
  if (movement.type === 'ADJUSTMENT') return reason ?? movementTypeLabels.ADJUSTMENT
  if (movement.type === 'CONSUMPTION' && movement.qty > 0) {
    return movement.sourceType === 'ORDER_VOID' ? 'Void' : 'Sale reversed'
  }
  if (movement.type === 'CONSUMPTION' && movement.sourceType === 'PRODUCTION_RUN') {
    return 'Used in production'
  }
  return movementTypeLabels[movement.type]
}

const SOURCE_PREFIXES: Record<MovementSourceType, string> = {
  ORDER_FIRE: 'SALE',
  ORDER_VOID: 'VOID',
  ORDER_WASTE: 'WASTE',
  ORDER_COMP: 'COMP',
  SHIPMENT: 'SHIP',
  REFUND: 'REF',
  PURCHASE_ORDER: 'GRN',
  STOCK_COUNT: 'COUNT',
  COUNT_ANCHOR: 'COUNT',
  TRANSFER: 'TRF',
  ADJUSTMENT: 'ADJ',
  PRODUCTION_RUN: 'PROD',
  SYSTEM: 'SYS',
  ORDER: 'SALE',
  POS_TRANSACTION: 'SALE',
  WASTAGE: 'WASTE',
}

/**
 * `SALE-9F21`, `COUNT-0007`. The service keeps only the source's id, so the
 * reference is its kind and the id's first four characters — short enough to
 * scan, with the whole id on hover. A back-office wastage reads as `WASTE`.
 */
function sourceRef(movement: StockMovement): string {
  const prefix =
    movement.sourceType === 'ADJUSTMENT' && movement.type === 'WASTE'
      ? 'WASTE'
      : SOURCE_PREFIXES[movement.sourceType]

  return `${prefix}-${movement.sourceId.replace(/-/g, '').slice(0, 4).toUpperCase()}`
}

const occurredDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const occurredTime = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** `Sep 8 · 08:12`. The full date, year included, is on hover. */
function occurred(iso: string): string {
  const date = new Date(iso)
  return `${occurredDay.format(date)} · ${occurredTime.format(date)}`
}

interface MovementsProps {
  filters: MovementFilters
  locations: readonly InventoryLocation[]
  onTypeChange: (type: MovementType | null) => void
}

function Movements({ filters, locations, onTypeChange }: MovementsProps) {
  const [page, setPage] = usePageFor(`${filters.locationId ?? ''}|${filters.type ?? ''}`)
  const { movements, total, isLoading, isRefreshing, error, retry } = useStockMovements(
    filters,
    page,
    PAGE_SIZE,
  )
  const items = useItemsOf(movements)
  const { byId: reasons } = useReasonCodes()
  const { tenant } = useTenant()

  /* The page on screen, as the reports' exports are: what is being looked at,
     filters and all. */
  function exportPage() {
    const location = filters.locationId
      ? locationName(filters.locationId, locations)
      : 'All locations'

    void previewExcel({
      fileName: excelReportFileName('Stock-Movements', `${booksToday()}_page-${page + 1}`),
      sheetName: 'Stock Movements',
      business: translated(tenant?.name, ''),
      title: 'Stock Movements',
      lines: excelMomentLines(
        `Type: ${filters.type ? movementTypeLabels[filters.type] : 'All types'} · Page ${page + 1}`,
        location,
      ),
      columns: [
        { header: 'Occurred', type: 'datetime' },
        { header: 'Item', type: 'text', width: 30 },
        { header: 'Item Code', type: 'text', width: 14, optional: true },
        { header: 'Location', type: 'text', width: 24 },
        { header: 'Type', type: 'text', width: 20 },
        { header: 'Qty', type: 'qty' },
        { header: 'Unit', type: 'text', width: 8 },
        { header: 'Source', type: 'text' },
        { header: 'Source ID', type: 'text', width: 38, optional: true },
        { header: 'Notes', type: 'text', width: 30, optional: true },
      ],
      rows: movements.map((movement) => {
        const item = items.get(movement.itemId)

        return [
          movement.occurredAt,
          item?.name ?? movement.itemId,
          item?.sku,
          locationName(movement.locationId, locations),
          movementLabel(movement, reasons),
          movement.qty,
          movement.unitOfMeasure,
          sourceRef(movement),
          movement.sourceId,
          movement.notes,
        ]
      }),
      notes: ['This page of the ledger only — page through to export the rest.'],
      empty: 'No stock movements',
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect
          label="Type"
          allLabel="All types"
          value={filters.type}
          options={(Object.keys(movementTypeLabels) as MovementType[]).map((value) => ({
            value,
            label: movementTypeLabels[value],
          }))}
          onChange={(value) => onTypeChange(value as MovementType | null)}
        />

        <Tooltip delay={300}>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={movements.length === 0}
            onPress={exportPage}
            className="border-border bg-background h-9 rounded-xl border px-3 text-sm font-medium"
          >
            <DownloadIcon width={16} height={16} />
            Export
          </Button>
          <Tooltip.Content>Downloads the rows on this page as Excel</Tooltip.Content>
        </Tooltip>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="the stock movements" error={error} onRetry={retry} />
      ) : movements.length === 0 ? (
        filters.type ? (
          <EmptyState
            title="Nothing matches this type."
            description="Show every type to see all the movements here."
            action={{ label: 'Show all types', onPress: () => onTypeChange(null) }}
          />
        ) : (
          <EmptyState
            title="No movements yet."
            description="Every receipt, sale, count and transfer is recorded here as it happens."
          />
        )
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <MovementsTable movements={movements} items={items} locations={locations} />
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </>
  )
}

interface MovementsTableProps {
  movements: StockMovement[]
  items: ReadonlyMap<string, StockItem>
  locations: readonly InventoryLocation[]
}

function MovementsTable({ movements, items, locations }: MovementsTableProps) {
  const { catalog: units } = useUnitCatalog()
  const { byId: reasons } = useReasonCodes()

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Movement ledger">
          <Table.Header>
            <Table.Column id="occurred" isRowHeader>
              Occurred
            </Table.Column>
            <Table.Column id="item">Item</Table.Column>
            <Table.Column id="branch">Branch</Table.Column>
            <Table.Column id="type">Type</Table.Column>
            <Table.Column id="qty" className="text-right">
              Qty
            </Table.Column>
            <Table.Column id="source">Source</Table.Column>
          </Table.Header>

          <Table.Body>
            {movements.map((movement) => (
              <Table.Row key={movement.id} id={movement.id}>
                <Table.Cell>
                  <span
                    title={dateTime(movement.occurredAt)}
                    className="font-mono text-xs whitespace-nowrap tabular-nums"
                  >
                    {occurred(movement.occurredAt)}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="font-medium">{items.get(movement.itemId)?.name ?? '…'}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted">{locationName(movement.locationId, locations)}</span>
                </Table.Cell>

                <Table.Cell>
                  <Chip size="sm" variant="soft">
                    {movementLabel(movement, reasons)}
                  </Chip>
                </Table.Cell>

                <Table.Cell>
                  <span className="flex flex-col items-end">
                    <span className={FIGURE_CLASS}>
                      {measured(movement.qty, movement.unitOfMeasure, units)}
                    </span>
                    {movement.shortfallQty > 0 && (
                      <span className="text-danger text-xs">
                        {quantity(movement.shortfallQty)} {movement.unitOfMeasure} not on hand
                      </span>
                    )}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span title={movement.sourceId} className="text-muted font-mono text-xs">
                    {sourceRef(movement)}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
